// ---------------------------------------------------------------------------
// Content Universe Service
// ---------------------------------------------------------------------------
// API client for Funnel Insights (Content Universe) endpoints.
// Follows funnelService pattern (object with async methods).
// ---------------------------------------------------------------------------

import { api } from '@/lib/api'
import type { FunnelLayerInput } from './funnelService'
import type {
  ContentUniverseOverviewResponse,
  ContentUniversePeopleResponse,
  ContentUniverseContentResponse,
  ContentUniverseCommentsResponse,
  ContentUniverseBrandsResponse,
  ContentUniverseTeamsResponse,
  CommentsSummaryResponse,
  ContentSortField,
  CommentSortField,
  SortOrder,
} from '@/components/v11/contentUniverse/types'

// ---------------------------------------------------------------------------
// Base URL
// ---------------------------------------------------------------------------

const BASE = '/api/v11/discovery/funnel/insights'

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const contentUniverseService = {
  /**
   * Get aggregated overview stats for the filtered audience.
   */
  async getOverview(layers: readonly FunnelLayerInput[]): Promise<ContentUniverseOverviewResponse> {
    const response = await api.post<ContentUniverseOverviewResponse>(
      `${BASE}/overview`,
      { layers }
    )
    return response as unknown as ContentUniverseOverviewResponse
  },

  /**
   * Get top N profiles for the people mosaic.
   */
  async getPeople(
    layers: readonly FunnelLayerInput[],
    limit = 50
  ): Promise<ContentUniversePeopleResponse> {
    const response = await api.post<ContentUniversePeopleResponse>(
      `${BASE}/people`,
      { layers, limit }
    )
    return response as unknown as ContentUniversePeopleResponse
  },

  /**
   * Get posts the filtered audience commented on.
   */
  async getContent(
    layers: readonly FunnelLayerInput[],
    page = 1,
    limit = 25,
    sort: ContentSortField = 'audience_count',
    order: SortOrder = 'desc'
  ): Promise<ContentUniverseContentResponse> {
    const response = await api.post<ContentUniverseContentResponse>(
      `${BASE}/content`,
      { layers, page, limit, sort, order }
    )
    return response as unknown as ContentUniverseContentResponse
  },

  /**
   * Get aggregated comments summary (emotions, sentiment, emojis, top comments with profiles).
   */
  async getCommentsSummary(
    layers: readonly FunnelLayerInput[]
  ): Promise<CommentsSummaryResponse> {
    const response = await api.post<CommentsSummaryResponse>(
      `${BASE}/comments/summary`,
      { layers }
    )
    return response as unknown as CommentsSummaryResponse
  },

  /**
   * Get comments from the filtered audience.
   */
  async getComments(
    layers: readonly FunnelLayerInput[],
    page = 1,
    limit = 25,
    sort: CommentSortField = 'comment_date',
    order: SortOrder = 'desc'
  ): Promise<ContentUniverseCommentsResponse> {
    const response = await api.post<ContentUniverseCommentsResponse>(
      `${BASE}/comments`,
      { layers, page, limit, sort, order }
    )
    return response as unknown as ContentUniverseCommentsResponse
  },

  /**
   * Get brand affinity for the filtered audience.
   */
  async getBrands(
    layers: readonly FunnelLayerInput[],
    page = 1,
    limit = 25
  ): Promise<ContentUniverseBrandsResponse> {
    const response = await api.post<ContentUniverseBrandsResponse>(
      `${BASE}/brands`,
      { layers, page, limit }
    )
    return response as unknown as ContentUniverseBrandsResponse
  },

  /**
   * Get team loyalty for the filtered audience.
   */
  async getTeams(
    layers: readonly FunnelLayerInput[],
    page = 1,
    limit = 25
  ): Promise<ContentUniverseTeamsResponse> {
    const response = await api.post<ContentUniverseTeamsResponse>(
      `${BASE}/teams`,
      { layers, page, limit }
    )
    return response as unknown as ContentUniverseTeamsResponse
  },
}
